import { Link } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { Sun, Moon, ArrowLeft, FileText } from 'lucide-react';
import { MetaTradifyLogo, MetaTradifyLogoSmall } from '@/components/MetaTradifyLogo';

const sections = [
  {
    title: '1. Information We Collect',
    content: [
      { subtitle: 'Personal Information', text: 'When you register, we collect your name, email address, phone number, and profile picture. For KYC verification, we may collect government-issued ID documents, address proof, and selfie photographs.' },
      { subtitle: 'Financial Information', text: 'We collect wallet balances, trading history, deposit/withdrawal records, and UPI IDs for payment processing. We do not store credit card or debit card numbers directly.' },
      { subtitle: 'Usage Data', text: 'We automatically collect information about your interactions with the platform, including pages visited, features used, trading patterns, device information, IP address, browser type, and session duration.' },
      { subtitle: 'Cookies & Tracking', text: 'We use essential cookies for authentication (JWT tokens) and session management. We do not use third-party advertising cookies. Analytics cookies help us understand platform usage to improve our services.' },
    ],
  },
  {
    title: '2. How We Use Your Information',
    content: [
      { text: 'We use collected information for the following purposes:' },
      { text: 'Account Management: To create, maintain, and secure your trading account, process transactions, and provide customer support.' },
      { text: 'Trading Services: To execute trades, calculate wallet balances, generate trade reports, and deliver real-time market data.' },
      { text: 'Security & Compliance: To verify your identity (KYC), prevent fraud, detect unauthorized access, and comply with applicable financial regulations.' },
      { text: 'Platform Improvement: To analyze usage patterns, fix bugs, optimize performance, and develop new features based on aggregated, anonymized data.' },
      { text: 'Communications: To send transactional emails (trade confirmations, password resets), security alerts, and important platform updates. You can opt out of non-essential communications.' },
    ],
  },
  {
    title: '3. Data Sharing & Disclosure',
    content: [
      { text: 'We do not sell your personal information to third parties. We may share your data only in the following circumstances:' },
      { subtitle: 'Service Providers', text: 'We work with trusted third-party services for payment processing (UPI providers), email delivery, cloud hosting, and market data. These providers are bound by strict confidentiality agreements.' },
      { subtitle: 'Legal Requirements', text: 'We may disclose information if required by law, court order, or government regulation, or to protect the rights, property, or safety of metaTradify, our users, or the public.' },
      { subtitle: 'Business Transfers', text: 'In the event of a merger, acquisition, or sale of assets, user data may be transferred. We will notify you of any such change and your options regarding your data.' },
    ],
  },
  {
    title: '4. Data Security',
    content: [
      { text: 'We implement industry-standard security measures to protect your data:' },
      { text: 'Encryption: All data is encrypted in transit (TLS/SSL) and at rest (AES-256). Passwords are hashed using bcrypt with salt.' },
      { text: 'Access Control: Role-based access control (RBAC) ensures only authorized personnel can access sensitive data. Two-factor authentication (2FA) is available for all accounts.' },
      { text: 'Infrastructure: Our servers are hosted in secure cloud environments with regular security audits, automated backups, and DDoS protection.' },
      { text: 'Monitoring: We employ real-time monitoring and logging to detect and respond to security incidents promptly.' },
    ],
  },
  {
    title: '5. Data Retention',
    content: [
      { text: 'We retain your personal data for as long as your account is active or as needed to provide services. After account deletion, we retain certain data for up to 5 years as required by financial regulations.' },
      { text: 'Trading history and transaction records are retained for 7 years in compliance with financial reporting requirements. Anonymized, aggregated data may be retained indefinitely for analytics purposes.' },
    ],
  },
  {
    title: '6. Your Rights',
    content: [
      { text: 'You have the following rights regarding your personal data:' },
      { text: 'Access: Request a copy of the personal data we hold about you.' },
      { text: 'Correction: Update or correct inaccurate information in your profile.' },
      { text: 'Deletion: Request deletion of your account and associated data, subject to legal retention requirements.' },
      { text: 'Portability: Request your data in a machine-readable format.' },
      { text: 'Objection: Object to certain processing of your data, including marketing communications.' },
      { text: 'To exercise any of these rights, contact us at privacy@metatradify.com or through the Contact Us page.' },
    ],
  },
  {
    title: '7. Third-Party Services',
    content: [
      { text: 'Our platform integrates with third-party services that have their own privacy policies:' },
      { text: 'Market Data Providers: Binance US and Twelve Data provide real-time market prices. Your trading activity is not shared with these providers.' },
      { text: 'Google OAuth: If you sign in with Google, we receive your name, email, and profile picture from Google. We do not access your Google contacts, files, or other data.' },
      { text: 'TradingView: Chart widgets are provided by TradingView. TradingView may collect usage analytics through their embedded widgets.' },
    ],
  },
  {
    title: '8. Children\'s Privacy',
    content: [
      { text: 'metaTradify is not intended for users under the age of 18. We do not knowingly collect personal information from minors. If we learn that we have collected data from a user under 18, we will promptly delete the account and associated data.' },
    ],
  },
  {
    title: '9. Changes to This Policy',
    content: [
      { text: 'We may update this Privacy Policy from time to time. When we make material changes, we will notify you via email or a prominent notice on the platform. Your continued use of metaTradify after changes take effect constitutes acceptance of the updated policy.' },
    ],
  },
  {
    title: '10. Contact Us',
    content: [
      { text: 'If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:' },
      { text: 'Email: privacy@metatradify.com' },
      { text: 'Address: metaTradify Technologies Pvt. Ltd., Cyber City, Gurugram, Haryana 122002, India' },
      { text: 'You may also reach us through our Contact Us page for any privacy-related inquiries.' },
    ],
  },
];

export default function PrivacyPolicyPage() {
  const { theme, toggleTheme } = useTheme();

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)]">
      {/* Nav */}
      <nav className="fixed top-0 w-full z-50 glass border-b border-[var(--border-subtle)]" data-testid="privacy-nav">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <MetaTradifyLogo size={32} />
            <span className="text-lg font-bold font-['Outfit'] tracking-tight">metaTradify</span>
          </Link>
          <div className="flex items-center gap-3">
            <button onClick={toggleTheme} className="p-2 rounded-lg bg-[var(--hover-bg)] border border-[var(--border-subtle)] transition-all">
              {theme === 'dark' ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4 text-green-500" />}
            </button>
            <Link to="/" className="flex items-center gap-1.5 px-4 py-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back
            </Link>
          </div>
        </div>
      </nav>

      <div className="pt-28 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[var(--border-subtle)] bg-[var(--hover-bg)] text-xs text-[var(--text-secondary)] mb-6">
              <FileText className="w-3 h-3 text-green-400" /> Legal
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold font-['Outfit'] tracking-tight leading-[1.1] mb-4" data-testid="privacy-heading">
              Privacy <span className="text-green-400">Policy</span>
            </h1>
            <p className="text-sm text-[var(--text-secondary)]">
              Last updated: April 2, 2026 &middot; Effective immediately
            </p>
            <p className="text-base text-[var(--text-secondary)] mt-4 max-w-2xl leading-relaxed">
              At metaTradify, we take your privacy seriously. This policy describes how we collect, use, protect, and share your personal information when you use our trading platform.
            </p>
          </div>

          {/* Table of Contents */}
          <div className="p-5 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg mb-10" data-testid="privacy-toc">
            <h2 className="text-sm font-semibold font-['Outfit'] mb-3 text-[var(--text-primary)]">Table of Contents</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {sections.map((s, i) => (
                <a key={i} href={`#section-${i}`} className="text-xs text-[var(--text-secondary)] hover:text-green-400 transition-colors py-1">
                  {s.title}
                </a>
              ))}
            </div>
          </div>

          {/* Sections */}
          <div className="space-y-10">
            {sections.map((section, i) => (
              <section key={i} id={`section-${i}`} className="scroll-mt-24" data-testid={`privacy-section-${i}`}>
                <h2 className="text-lg font-semibold font-['Outfit'] text-[var(--text-primary)] mb-4 pb-2 border-b border-[var(--border-subtle)]">
                  {section.title}
                </h2>
                <div className="space-y-3">
                  {section.content.map((item, j) => (
                    <div key={j}>
                      {item.subtitle && (
                        <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-1">{item.subtitle}</h3>
                      )}
                      <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{item.text}</p>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-[var(--border-subtle)] py-8 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <MetaTradifyLogoSmall size={24} />
            <span className="text-sm font-semibold font-['Outfit']">metaTradify</span>
          </div>
          <div className="flex items-center gap-6 text-xs text-[var(--text-tertiary)]">
            <Link to="/about" className="hover:text-[var(--text-secondary)] transition-colors">About</Link>
            <Link to="/contact" className="hover:text-[var(--text-secondary)] transition-colors">Contact</Link>
            <Link to="/privacy-policy" className="hover:text-[var(--text-secondary)] transition-colors">Privacy</Link>
          </div>
          <p className="text-xs text-[var(--text-tertiary)]">&copy; 2025 metaTradify. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
